import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Plus, Edit2, Trash2, Share2, CheckCircle, X, ExternalLink, ImageIcon, FileImage,
  Calendar, Clock, Users, IndianRupee, Target, TrendingUp, Copy, Zap
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PosterModal from "@/components/PosterModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const EMPTY_FORM = {
  title: "", description: "", date: "", time: "", instructor: "",
  price: "", duration: "", seats: "", image_url: "", status: "active",
  zoom_link: "", whatsapp_community_link: "",
  offer_enabled: false, offer_percentage: "", offer_end_date: ""
};

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  upcoming: "bg-blue-100 text-blue-700 border-blue-200",
  draft: "bg-amber-100 text-amber-700 border-amber-200",
  completed: "bg-gray-100 text-gray-600 border-gray-200"
};

// Training Card Component
function TrainingCard({ training, onEdit, onDelete, onPoster, onCopy, copied, onGenerateImage, generatingImage }) {
  const fillRate = training.seats > 0 ? Math.round((training.seats_filled || 0) / training.seats * 100) : 0;
  const seatsLeft = training.seats - (training.seats_filled || 0);
  const estimatedRevenue = (training.seats_filled || 0) * (training.price || 0);
  
  // Offer calculations
  const hasOffer = training.offer_enabled && training.offer_percentage && training.offer_end_date;
  const offerEndDate = hasOffer ? new Date(training.offer_end_date) : null;
  const isOfferValid = hasOffer && offerEndDate && offerEndDate >= new Date();
  const offerPrice = isOfferValid ? Math.round(training.price * (1 - training.offer_percentage / 100)) : training.price;
  
  const hasImage = training.poster_image_data || training.image_url;
  
  // Fill rate color
  const getFillRateColor = () => {
    if (fillRate >= 80) return "from-emerald-500 to-emerald-400";
    if (fillRate >= 50) return "from-blue-500 to-blue-400";
    if (fillRate >= 25) return "from-amber-500 to-amber-400";
    return "from-gray-400 to-gray-300";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`training-card-${training.id}`}
      className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 group"
    >
      {/* Top Section with Image */}
      <div className="relative">
        {/* Image */}
        <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
          {hasImage ? (
            <img 
              src={training.poster_image_data || training.image_url} 
              alt={training.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <ImageIcon size={32} className="text-gray-300" />
              <button
                onClick={() => onGenerateImage(training.id)}
                disabled={generatingImage === training.id}
                className="text-xs font-bold px-3 py-1.5 bg-purple-500 text-white hover:bg-purple-600 disabled:bg-purple-300 transition-colors flex items-center gap-1"
              >
                {generatingImage === training.id ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap size={12} />
                    Generate AI Image
                  </>
                )}
              </button>
            </div>
          )}
          {/* Gradient Overlay */}
          {hasImage && <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />}
        </div>
        
        {/* Status Badge */}
        <div className="absolute top-3 left-3">
          <span className={`text-[10px] font-bold px-2 py-1 border ${STATUS_COLORS[training.status] || STATUS_COLORS.active}`}>
            {training.status?.toUpperCase()}
          </span>
        </div>
        
        {/* Offer Badge */}
        {isOfferValid && (
          <div className="absolute top-3 right-3">
            <span className="text-[10px] font-bold px-2 py-1 bg-red-500 text-white animate-pulse">
              {training.offer_percentage}% OFF
            </span>
          </div>
        )}
        
        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-outfit font-bold text-white text-lg leading-tight line-clamp-2 drop-shadow-lg">
            {training.title}
          </h3>
        </div>
      </div>
      
      {/* Content Section */}
      <div className="p-4 space-y-4">
        {/* Meta Info Row */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 font-dm-sans">
          <div className="flex items-center gap-1">
            <Calendar size={12} className="text-gray-400" />
            <span>{training.date}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={12} className="text-gray-400" />
            <span>{training.duration}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users size={12} className="text-gray-400" />
            <span>{training.instructor}</span>
          </div>
        </div>
        
        {/* Fill Rate Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-dm-sans">Fill Rate</span>
            <span className={`font-bold ${fillRate >= 80 ? 'text-emerald-600' : fillRate >= 50 ? 'text-blue-600' : 'text-gray-600'}`}>
              {fillRate}%
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${fillRate}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`h-full bg-gradient-to-r ${getFillRateColor()} rounded-full`}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-400 font-dm-sans">
            <span>{training.seats_filled || 0} registered</span>
            <span>{seatsLeft} seats left</span>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Price */}
          <div className="bg-gray-50 p-3 rounded">
            <div className="flex items-center gap-1 text-gray-400 text-[10px] uppercase tracking-wide mb-1">
              <IndianRupee size={10} />
              <span>Price</span>
            </div>
            {isOfferValid ? (
              <div className="flex items-center gap-2">
                <span className="font-outfit font-bold text-gray-900">₹{offerPrice.toLocaleString('en-IN')}</span>
                <span className="text-xs text-gray-400 line-through">₹{training.price?.toLocaleString('en-IN')}</span>
              </div>
            ) : (
              <span className="font-outfit font-bold text-gray-900">₹{training.price?.toLocaleString('en-IN')}</span>
            )}
          </div>
          
          {/* Revenue */}
          <div className="bg-emerald-50 p-3 rounded">
            <div className="flex items-center gap-1 text-emerald-500 text-[10px] uppercase tracking-wide mb-1">
              <TrendingUp size={10} />
              <span>Revenue</span>
            </div>
            <span className="font-outfit font-bold text-emerald-700">₹{estimatedRevenue.toLocaleString('en-IN')}</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button
            data-testid={`poster-btn-${training.id}`}
            onClick={() => onPoster(training)}
            title="View/Generate Poster"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-purple-600 bg-purple-50 hover:bg-purple-100 transition-colors"
          >
            <FileImage size={13} />
            Poster
          </button>
          <button
            data-testid={`copy-btn-${training.id}`}
            onClick={() => onCopy(training)}
            title="Copy Registration Link"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            {copied === training.id ? <CheckCircle size={13} /> : <Share2 size={13} />}
            {copied === training.id ? "Copied!" : "Share"}
          </button>
          <a
            href={`${window.location.origin}/trainings/${training.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View Public Page"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors rounded"
          >
            <ExternalLink size={14} />
          </a>
          <button
            data-testid={`edit-btn-${training.id}`}
            onClick={() => onEdit(training)}
            title="Edit Training"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors rounded"
          >
            <Edit2 size={14} />
          </button>
          <button
            data-testid={`delete-btn-${training.id}`}
            onClick={() => onDelete(training.id)}
            title="Delete Training"
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors rounded"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminTrainings() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTraining, setEditTraining] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [posterTraining, setPosterTraining] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(null);

  const fetchTrainings = async () => {
    try {
      const r = await axios.get(`${API}/admin/trainings`, authHeader());
      setTrainings(r.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchTrainings(); }, []);

  const handleGenerateImage = async (trainingId) => {
    setGeneratingImage(trainingId);
    try {
      await axios.post(`${API}/admin/trainings/${trainingId}/generate-image`, {}, authHeader());
      await fetchTrainings(); // Refresh to show new image
    } catch (err) {
      console.error("Failed to generate image:", err);
      alert("Failed to generate image. Please try again.");
    } finally {
      setGeneratingImage(null);
    }
  };

  const openCreate = () => {
    setEditTraining(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditTraining(t);
    setForm({
      title: t.title || "", description: t.description || "",
      date: t.date || "", time: t.time || "", instructor: t.instructor || "",
      price: t.price?.toString() || "", duration: t.duration || "",
      seats: t.seats?.toString() || "", image_url: t.image_url || "", status: t.status || "active",
      zoom_link: t.zoom_link || "", whatsapp_community_link: t.whatsapp_community_link || "",
      offer_enabled: t.offer_enabled || false,
      offer_percentage: t.offer_percentage?.toString() || "",
      offer_end_date: t.offer_end_date || ""
    });
    setError("");
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title || !form.date || !form.instructor || !form.price || !form.seats) {
      setError("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        seats: parseInt(form.seats),
        offer_percentage: form.offer_enabled && form.offer_percentage ? parseInt(form.offer_percentage) : null,
        offer_end_date: form.offer_enabled && form.offer_end_date ? form.offer_end_date : null
      };
      if (editTraining) {
        await axios.put(`${API}/admin/trainings/${editTraining.id}`, payload, authHeader());
      } else {
        await axios.post(`${API}/admin/trainings`, payload, authHeader());
      }
      setShowModal(false);
      fetchTrainings();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save training");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/trainings/${id}`, authHeader());
      fetchTrainings();
      setDeleteId(null);
    } catch {}
  };

  const copyLink = (t) => {
    const link = t.share_link || `${window.location.origin}/trainings/${t.id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(t.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-gray-900">Training Management</h1>
          <p className="text-gray-500 text-sm font-dm-sans mt-1">Create and manage your AI training posters</p>
        </div>
        <button
          data-testid="create-training-btn"
          onClick={openCreate}
          className="flex items-center gap-2 bg-black text-white font-outfit font-bold px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors"
        >
          <Plus size={16} /> New Training
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 h-80 animate-pulse">
              <div className="h-36 bg-gray-100" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="h-2 bg-gray-100 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : trainings.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 p-16 text-center">
          <ImageIcon size={40} className="text-gray-300 mx-auto mb-3" />
          <h3 className="font-outfit font-bold text-gray-700 text-lg mb-1">No trainings yet</h3>
          <p className="text-gray-400 text-sm font-dm-sans mb-4">Create your first AI training poster</p>
          <button onClick={openCreate} className="bg-black text-white font-outfit font-bold px-5 py-2.5 text-sm hover:bg-gray-800 transition-colors">
            Create Training
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainings.map((t, i) => (
            <TrainingCard
              key={t.id}
              training={t}
              onEdit={openEdit}
              onDelete={setDeleteId}
              onPoster={setPosterTraining}
              onCopy={copyLink}
              copied={copied}
              onGenerateImage={handleGenerateImage}
              generatingImage={generatingImage}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-gray-900">
              {editTraining ? "Edit Training" : "Create New Training"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Title *</label>
                <input
                  data-testid="training-title-input"
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="e.g. Advanced ML with Python"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Description *</label>
                <textarea
                  data-testid="training-desc-input"
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm resize-none"
                  rows={3}
                  placeholder="Describe what participants will learn..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Date *</label>
                <input
                  data-testid="training-date-input"
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Time *</label>
                <input
                  data-testid="training-time-input"
                  type="time"
                  value={form.time}
                  onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Instructor *</label>
                <input
                  data-testid="training-instructor-input"
                  value={form.instructor}
                  onChange={e => setForm(p => ({ ...p, instructor: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="Instructor name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Duration *</label>
                <input
                  data-testid="training-duration-input"
                  value={form.duration}
                  onChange={e => setForm(p => ({ ...p, duration: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="e.g. 8 Hours / 3 Days"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Price (INR) *</label>
                <input
                  data-testid="training-price-input"
                  type="number"
                  value={form.price}
                  onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="e.g. 2999"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Total Seats *</label>
                <input
                  data-testid="training-seats-input"
                  type="number"
                  value={form.seats}
                  onChange={e => setForm(p => ({ ...p, seats: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="e.g. 30"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Status</label>
                <select
                  data-testid="training-status-select"
                  value={form.status}
                  onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                >
                  <option value="active">Active (Public)</option>
                  <option value="draft">Draft (Hidden)</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Image URL (optional)</label>
                <input
                  data-testid="training-image-input"
                  value={form.image_url}
                  onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Zoom Link (optional)</label>
                <input
                  data-testid="training-zoom-link-input"
                  value={form.zoom_link}
                  onChange={e => setForm(p => ({ ...p, zoom_link: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="https://zoom.us/j/..."
                />
                <p className="text-xs text-gray-400 mt-1 font-dm-sans">This link will be sent to participants after payment</p>
              </div>
              
              {/* Offer Section */}
              <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="offer_enabled"
                    data-testid="offer-enabled-checkbox"
                    checked={form.offer_enabled}
                    onChange={e => setForm(p => ({ ...p, offer_enabled: e.target.checked }))}
                    className="w-4 h-4 accent-black"
                  />
                  <label htmlFor="offer_enabled" className="text-sm font-medium text-gray-700 font-dm-sans">
                    Enable Price Drop Offer
                  </label>
                </div>
                
                {form.offer_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Discount Percentage *</label>
                      <div className="relative">
                        <input
                          data-testid="offer-percentage-input"
                          type="number"
                          value={form.offer_percentage}
                          onChange={e => setForm(p => ({ ...p, offer_percentage: e.target.value }))}
                          className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm pr-10"
                          placeholder="e.g. 50"
                          min="1"
                          max="99"
                          required={form.offer_enabled}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-dm-sans">%</span>
                      </div>
                      {form.price && form.offer_percentage && (
                        <p className="text-xs text-green-600 mt-1 font-dm-sans font-medium">
                          Offer Price: ₹{Math.round(parseFloat(form.price) * (1 - parseInt(form.offer_percentage) / 100)).toLocaleString('en-IN')} 
                          <span className="text-gray-500 ml-1">(Save ₹{Math.round(parseFloat(form.price) * parseInt(form.offer_percentage) / 100).toLocaleString('en-IN')})</span>
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Offer End Date *</label>
                      <input
                        data-testid="offer-end-date-input"
                        type="date"
                        value={form.offer_end_date}
                        onChange={e => setForm(p => ({ ...p, offer_end_date: e.target.value }))}
                        className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                        required={form.offer_enabled}
                      />
                      <p className="text-xs text-gray-500 mt-1 font-dm-sans">Offer will be displayed until this date</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">WhatsApp Community Link (optional)</label>
                <input
                  data-testid="training-whatsapp-link-input"
                  value={form.whatsapp_community_link}
                  onChange={e => setForm(p => ({ ...p, whatsapp_community_link: e.target.value }))}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  placeholder="https://chat.whatsapp.com/..."
                />
                <p className="text-xs text-gray-400 mt-1 font-dm-sans">Participants can join this group after payment</p>
              </div>
            </div>
            {error && <p className="text-red-500 text-sm font-dm-sans">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button
                data-testid="save-training-btn"
                type="submit"
                disabled={saving}
                className="flex-1 bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60"
              >
                {saving ? "Saving..." : editTraining ? "Update Training" : "Create Training"}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 border border-gray-300 text-gray-700 font-dm-sans text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-gray-900">Delete Training</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 text-sm font-dm-sans">Are you sure you want to delete this training? This action cannot be undone.</p>
          <div className="flex gap-3 mt-4">
            <button
              data-testid="confirm-delete-btn"
              onClick={() => handleDelete(deleteId)}
              className="flex-1 bg-red-600 text-white font-outfit font-bold py-2.5 hover:bg-red-700 transition-colors text-sm"
            >
              Delete
            </button>
            <button
              onClick={() => setDeleteId(null)}
              className="flex-1 border border-gray-300 text-gray-700 font-dm-sans text-sm py-2.5 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Poster Modal */}
      {posterTraining && (
        <PosterModal training={posterTraining} onClose={() => setPosterTraining(null)} />
      )}
    </div>
  );
}
